<template>
  <div class="message-container">
    <div class="message-body">
      <div class="message-title">
        <h3 class="message-title-regular">{{ title }}</h3>
        <i v-if="type === 1" class="time">{{ time }}</i>
      </div>

      <template v-if="contentType === 1">
        <iframe v-if="pdfScriptsLoaded" style="border-radius: 8px" class="w-full h-450" :src="fileUrl"></iframe>
        <div v-else class="loading-pdf">Loading PDF viewer...</div>
      </template>
      <template v-else>
        <div class="message-content">
          <div @click="$emit('click', $event)" v-html="content"></div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MessageContent',
  props: {
    title: {
      type: String,
      default: ''
    },
    time: {
      type: String,
      default: ''
    },
    type: {
      type: Number,
      default: 1
    },
    content: {
      type: String,
      default: ''
    },
    contentType: {
      type: Number,
      default: 0
    },
    url: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      pdfScriptsLoaded: false
    }
  },
  computed: {
    fileUrl() {
      return './pdfjs/web/viewer.html?file=' + this.pdfUrl
    },
    pdfUrl() {
      const slashs = this.url.split('/')
      const fileName = slashs[slashs.length - 1]
      return encodeURIComponent(`/common/urlDownload?fileName=${fileName}&url=${this.url}`)
    }
  },
  watch: {
    contentType(newValue) {
      if (newValue === 1 && this.url && !this.pdfScriptsLoaded) {
        this.loadPdfScripts()
      }
    },
    url(newValue) {
      if (this.contentType === 1 && newValue && !this.pdfScriptsLoaded) {
        this.loadPdfScripts()
      }
    }
  },
  mounted() {
    if (this.contentType === 1 && this.url) {
      this.loadPdfScripts()
    }
  },
  methods: {
    loadPdfScripts() {
      // Check if scripts are already loaded
      if (document.querySelector('script[data-pdf-script="worker"]')) {
        this.pdfScriptsLoaded = true
        return Promise.resolve()
      }

      // Load PDF.js scripts dynamically
      return Promise.all([
        this.loadScript('./pdfjs/build/pdf.worker.js', 'worker'),
        this.loadScript('./pdfjs/build/pdf.js', 'main'),
        this.loadScript('./pdfjs/web/viewer.js', 'viewer')
      ])
        .then(() => {
          this.pdfScriptsLoaded = true
          console.log('PDF.js scripts loaded successfully')
        })
        .catch((error) => {
          console.error('Failed to load PDF.js scripts:', error)
        })
    },
    loadScript(src, id) {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.src = src
        script.async = true
        script.setAttribute('data-pdf-script', id)

        script.onload = () => resolve()
        script.onerror = () => reject(new Error(`Failed to load script: ${src}`))

        document.head.appendChild(script)
      })
    }
  }
}
</script>

<style lang="scss">
.message-container {
  height: 100%;

  .message-body {
    padding: 48px;
    background-image: linear-gradient(
      180deg,
      #132039 141px,
      #4b6eb4 282px,
      theme('colors.bgColor.global') 282px,
      theme('colors.bgColor.global') 100%
    );
    border-radius: theme('borderRadius.small');

    @include global.response_to(('mobile', 'pad')) {
      & {
        padding: 12px;
      }
    }

    .message-title {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding-bottom: 24px;

      .message-title-regular {
        font-family: theme('fontFamily.pingFang');
        font-size: 32px;
        font-weight: 600;
        line-height: 36px;
        color: theme('colors.assist.white');
        text-align: center;
      }

      .time {
        font-size: theme('fontSize.small');
      }
    }

    .message-content {
      min-height: 300px;
      max-height: 400px;
      padding: 48px;
      padding-top: 24px;
      overflow: auto;
      background: theme('colors.bgColor.module');
      border: 1px solid theme('colors.border');
      border-radius: theme('borderRadius.small');

      * {
        word-break: break-all;
      }

      @include global.response_to(('mobile', 'pad')) {
        & {
          height: 100%;
          max-height: 100%;
          padding: 12px;
          overflow: hidden;
        }
      }
    }

    .loading-pdf {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 450px;
      padding: 24px;
      font-size: 16px;
      text-align: center;
      background: theme('colors.bgColor.module');
      border: 1px solid theme('colors.border');
      border-radius: theme('borderRadius.small');
    }
  }
}
</style>
